import { Link } from "react-router-dom";
import { assetUrl } from "../api/client";
import type { FraudReport, FraudSignal, RecordingStatus } from "../api/types";
import FraudChip from "./FraudChip";
import { InfoTip } from "./Tooltip";
import { llmErrorLabel } from "../lib/fraud";

const LEVEL_LABEL: Record<FraudSignal["level"], string> = {
  certain: "Certain",
  strong: "Strong",
  weak: "Weak",
  info: "Info",
};

const SIGNAL_TIP: Record<string, string> = {
  emulator: "The device model is an Android emulator, not a phone.",
  duplicate_video: "The exact same video bytes were already uploaded as another recording (possibly from another account).",
  duplicate_imu: "The sensor stream is identical to another recording's, although the video differs.",
  imu_static: "The phone's gyroscope and accelerometer show it did not move for the whole recording.",
  pose_static: "ARCore tracked the camera but it travelled less than half a metre.",
  imu_repeat: "A stretch of sensor rows repeats byte-for-byte later in the file — copied data.",
  video_static: "Consecutive keyframes are pixel-identical for most of the recording: a frozen picture.",
  video_loop: "The same stretch of video recurs at a fixed lag: a segment was pasted more than once.",
  video_signature: "The video could not be sampled cheaply; the frame-based checks were skipped.",
};

function fmtValue(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    return Object.entries(v as Record<string, unknown>)
      .filter(([, x]) => x != null && typeof x !== "object")
      .map(([k, x]) => `${k.replace(/_/g, " ")}: ${typeof x === "number" ? Number(x.toFixed?.(2) ?? x) : String(x)}`)
      .join(" · ");
  }
  return null;
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "";
}

/** "Fraud check" card: the anti_fraud stage's verdict, every signal with its level, the
 * vision model's judgement when it was consulted, the duplicate it matched, the contact
 * sheet the model saw, and who decided what. */
export default function FraudCard({
  fraud,
  status,
}: {
  fraud: FraudReport;
  status: RecordingStatus;
}) {
  const llm = fraud.llm;
  const llmOk = Boolean(llm?.available);
  const likelihood = llmOk && llm?.fraud_likelihood != null ? Math.round(llm.fraud_likelihood * 100) : null;
  const decided = status === "fraud" || Boolean(fraud.cleared_at);

  return (
    <section className={`card fraud-card verdict-${fraud.verdict}`}>
      <div className="card-head">
        <h3>
          Fraud check{" "}
          <InfoTip content="Runs before the heavy pipeline stages. Cheap deterministic signals first (duplicate upload, emulator, still phone, looped video or sensor data, frozen picture); a vision model is consulted only for the grey zone. Certain signals or a high model likelihood park the recording here for a human decision." />
        </h3>
        <FraudChip fraud={fraud} status={status} />
      </div>

      {decided && (
        <p className={`fraud-decision ${status === "fraud" ? "confirmed" : "cleared"}`}>
          {status === "fraud" ? (
            <>
              Confirmed as fraud by <b>{fraud.confirmed_by ?? "a reviewer"}</b> on {when(fraud.confirmed_at)}
              {fraud.confirm_note && <> — “{fraud.confirm_note}”</>}
            </>
          ) : (
            <>
              Cleared by <b>{fraud.cleared_by ?? "a reviewer"}</b> on {when(fraud.cleared_at)}
              {fraud.clear_note && <> — “{fraud.clear_note}”</>}
            </>
          )}
        </p>
      )}

      {fraud.signals.length === 0 ? (
        <p className="muted small">No signals — the deterministic checks found nothing.</p>
      ) : (
        <ul className="fraud-signals">
          {fraud.signals.map((sg) => {
            const val = fmtValue(sg.value);
            return (
              <li key={sg.id} className={`fraud-signal-row ${sg.level}`}>
                <span className={`fraud-signal ${sg.level}`}>{LEVEL_LABEL[sg.level]}</span>
                <span className="fraud-signal-body">
                  <span className="fraud-signal-id">
                    {sg.id.replace(/_/g, " ")}
                    {SIGNAL_TIP[sg.id] && <InfoTip content={SIGNAL_TIP[sg.id]} />}
                  </span>
                  {sg.detail && <span className="fraud-signal-detail">{sg.detail}</span>}
                  {val && <span className="fraud-signal-value mono">{val}</span>}
                  {sg.id === "duplicate_video" && fraud.duplicate_of_recording_id != null && (
                    <Link to={`/r/${fraud.duplicate_of_recording_id}`} className="fraud-dup-link">
                      Open the original {fraud.duplicate_of_log_id ? `(${fraud.duplicate_of_log_id})` : ""} →
                    </Link>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <div className="fraud-llm">
        <div className="fraud-llm-head">
          <span className="ai-field-label">Vision model</span>
          {llmOk && likelihood != null ? (
            <span className={`fraud-likelihood ${likelihood >= 50 ? "high" : "low"}`}>
              {likelihood}% fraud likelihood
            </span>
          ) : (
            <span className="muted small">
              {fraud.llm_called
                ? `not available — ${llmErrorLabel(llm?.error) ?? "error"}`
                : (llmErrorLabel(llm?.error) ?? "model not consulted")}
            </span>
          )}
        </div>
        {llmOk && llm && (
          <>
            {llm.fraud_reasons && llm.fraud_reasons.length > 0 && (
              <ul className="fraud-reasons">
                {llm.fraud_reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            )}
            {llm.summary && <p className="ai-summary">{llm.summary}</p>}
            <dl className="ai-fields">
              {llm.task && (
                <>
                  <dt>Seen doing</dt>
                  <dd>{llm.task}</dd>
                </>
              )}
              {llm.task_matches != null && (
                <>
                  <dt>Matches declared task</dt>
                  <dd>{llm.task_matches ? "yes" : "no"}</dd>
                </>
              )}
              {llm.is_screen_recording && (
                <>
                  <dt>Screen recording</dt>
                  <dd>yes</dd>
                </>
              )}
              <dt>Model</dt>
              <dd className="mono small">
                {llm.model}
                {fraud.llm_cost_usd != null ? ` · $${fraud.llm_cost_usd.toFixed(4)}` : ""}
              </dd>
            </dl>
          </>
        )}
      </div>

      {fraud.sheet_url && (
        <a className="fraud-sheet" href={assetUrl(fraud.sheet_url)} target="_blank" rel="noreferrer" title="Frames sampled by the anti-fraud stage (opens full size)">
          <img src={assetUrl(fraud.sheet_url)} alt="Sampled frames" loading="lazy" />
        </a>
      )}

      <p className="muted small fraud-foot">
        {fraud.stage_seconds != null && <>checked in {fraud.stage_seconds.toFixed(1)} s</>}
        {fraud.alignment && <> · keyframes aligned by {fraud.alignment.replace(/_/g, " ")}</>}
        {fraud.video_sha256 && (
          <>
            {" "}
            · video sha256 <span className="mono">{fraud.video_sha256.slice(0, 12)}…</span>
          </>
        )}
      </p>
    </section>
  );
}
